import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { initialState, DataState } from "./types/initialState";
import { stdObject } from "../data/types/stdObject";

const dataSlice = createSlice({
  name: "data",
  initialState,
  reducers: {
    updateSync: (
      state,
      action: PayloadAction<{
        type: string | boolean;
        data: stdObject;
      }>,
    ) => {
      const { data } = action.payload;
      state.sync = data;
    },
    updateUsersList: (
      state,
      action: PayloadAction<{
        type: string;
        data: stdObject;
      }>,
    ) => {
      const { type, data } = action.payload;
      let tempUsersList: stdObject = state.usersList;
      tempUsersList[type] = data;
      state.usersList = tempUsersList;
    },
    addRolList: (
      state,
      action: PayloadAction<{
        data: stdObject;
      }>,
    ) => {
      const { data } = action.payload;
      state.rol.push(data);
    },
    updateRolsList: (
      state,
      action: PayloadAction<{
        type: string;
        data: stdObject;
      }>,
    ) => {
      const { type, data } = action.payload;
      const index = state.rol.findIndex((e: stdObject) => e.CODROL === type);
      state.rol[index] = data;
    },
    updateRowData: (
      state,
      action: PayloadAction<{
        type: string | boolean;
        data: Array<stdObject>;
      }>,
    ) => {
      const { type, data } = action.payload;
      state.listRow = {
        type,
        data,
      };
    },
    updateFieldData: (
      state,
      action: PayloadAction<{
        data: stdObject;
      }>,
    ) => {
      const { data } = action.payload;
      const index = state.per.findIndex(
        (e: stdObject) => e.TIPPER === data.TIPPER && e.CODPER === data.CODPER,
      );
      delete data.FIEPER;
      state.per[index] = data;
    },
    updateTable: (
      state,
      action: PayloadAction<{
        type: keyof DataState;
        index: number | boolean;
        data: stdObject;
      }>,
    ) => {
      const { type, index, data } = action.payload;
      if (
        type in state &&
        Array.isArray(state[type]) &&
        (type === "suc" ||
          type === "rol" ||
          type === "tal" ||
          type === "tar" ||
          type === "sda" ||
          type === "vtpa" ||
          type === "ctpa" ||
          type === "rut" ||
          type === "pag" ||
          type === "for" ||
          type === "hor" ||
          type === "are")
      ) {
        if (typeof index === "number") {
          if (index === -1) {
            state[type].push(data);
          } else {
            state[type][index] = data;
          }
        } else {
          const mainType = ["ctpa", "vtpa"].includes(type)
            ? "TPA"
            : type.toUpperCase();
          state[type] = state[type].filter(
            (e: stdObject) => e[`COD${mainType}`] !== data[`COD${mainType}`],
          );
          if (type === "rol") {
            let tempUserList: stdObject = state.usersList;
            Object.entries(tempUserList)
              .filter(([_, value]) => value.ROLUSU === data[`COD${mainType}`])
              .forEach(([key, _]) => {
                tempUserList[key].ROLUSU = 0;
              });
            state.usersList = tempUserList;
          }
        }
      }
    },
    updateItemData: (
      state,
      action: PayloadAction<{
        type: keyof DataState;
        data: stdObject;
      }>,
    ) => {
      const { type, data } = action.payload;
      if (type === "art") {
        state[type] = {
          ...state[type],
          ...data,
        };
      }
    },
    deletePersonItemData: (
      state,
      action: PayloadAction<{
        type: keyof DataState;
        code: string;
      }>,
    ) => {
      const { type, code } = action.payload;
      if (type === "art") {
        delete state[type][code];
      }
    },
    updateCat: (
      state,
      action: PayloadAction<{
        newCat: boolean;
        data: stdObject;
      }>,
    ) => {
      const { newCat, data } = action.payload;
      if (newCat) {
        state.cat.push(data);
      } else {
        const index = state.cat.findIndex(
          (e: stdObject) => e.CODCAT === data.CODCAT,
        );
        state.cat[index] = data;
      }
    },
    removeCat: (
      state,
      action: PayloadAction<{
        type: number;
      }>,
    ) => {
      const { type } = action.payload;
      state.cat = state.cat.filter((e: stdObject) => e.CODCAT !== type);
      state.cat = state.cat.map((e: stdObject) =>
        e.PADCAT === type.toString() ? { ...e, PADCAT: "" } : e,
      );
      let tempArt = state.art;
      Object.entries(tempArt)
        .filter(([_, value]) => value.FAMART === type)
        .forEach(([key, value]: [string, stdObject]) => {
          tempArt[key] = { ...value, FAMART: 0 };
        });
      state.art = tempArt;
    },
    updateHeaders: (
      state,
      action: PayloadAction<{
        data: stdObject[];
      }>,
    ) => {
      const { data } = action.payload;
      let tempHeaders: stdObject = state.headers;

      data.forEach((e: stdObject) => {
        const userKey = e.USUCAB ? "user" : "default";
        const index = tempHeaders[e.GRUCAB][userKey].findIndex(
          (v: stdObject) => v.CODCAB === e.CODCAB,
        );
        if (index > -1) {
          tempHeaders[e.GRUCAB][userKey][index] = e;
        }
      });

      state.headers = {
        ...tempHeaders,
      };
    },
    updateBrand: (
      state,
      action: PayloadAction<{
        isNew: boolean;
        data: stdObject;
      }>,
    ) => {
      const { isNew, data } = action.payload;
      if (isNew) {
        state.brands.push(data);
      } else {
        const index = state.brands.findIndex(
          (e: stdObject) => e.CODMAR === data.CODMAR,
        );
        state.brands[index] = data;
      }
    },
    deleteBrandData: (
      state,
      action: PayloadAction<{
        code: string;
      }>,
    ) => {
      const { code } = action.payload;
      state.brands = state.brands.filter((e: stdObject) => e.CODMAR !== code);
      Object.entries(state.art)
        .filter(([_, value]) => value.FTEART === code)
        .forEach(([key, _]) => {
          state.art[key].FTEART = null;
        });
    },
    saveAll: (state, action) => {
      Object.keys(action.payload).forEach((e) => {
        if (e && e in state) {
          state[e as keyof DataState] = action.payload[e];
        }
      });
    },
  },
});

export const {
  updateItemData,
  // updatePersonData,
  deletePersonItemData,
  updateCat,
  removeCat,
  updateHeaders,
  updateRowData,
  updateFieldData,
  updateTable,
  updateUsersList,
  addRolList,
  updateRolsList,
  updateBrand,
  deleteBrandData,
  saveAll,
} = dataSlice.actions;
export default dataSlice.reducer;
